import * as React from 'react';

import {
  Autocomplete,
  AutocompleteItem,
  AutocompleteItemBody,
  Note,
  PageDefaultIcon,
  PageDraftIcon,
  Select,
  View,
} from '@packages/systems/spring';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  selectAllCollections,
  selectCmsItemsForCurrentLocale,
} from '@packages/systems/cms/client-selectors';
import {
  useCollectionItem,
  useCollectionItemsPage,
} from '@packages/systems/cms/client';
import type {DomainRecord} from '@packages/systems/publish';
import type {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import {
  getItemsForCollection,
  isPublished,
} from '@packages/shared/utils/ItemUtils';
import {
  generateHrefMap,
  selectCollectionIdToPageIdMap,
} from '@packages/shared/utils/PageTreeUtils';
import {isMint} from '@packages/systems/core/utils/RecordUtils';
import {isSkusCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import useDebouncedValue from '@packages/systems/ui/hooks/useDebouncedValue';
import {createSelector} from 'reselect';

import type {State} from './LinkTypes';
import {Label} from './LinkUIComponents';

type ItemOption = {
  label: string;
  value: string;
  slug: string;
  isDraft: boolean;
};

export const selectLastPublished = (state: DesignerStores): string | null =>
  state.PublishDropdownUIStore.domains
    .map((domain: DomainRecord) => domain.get('lastPublished'))
    .filter(Boolean)
    .sort()
    .last() || null;

const selectPageStore = (state: DesignerStores) => state.PageStore;

const selectCollectionIdToPageIdMapFromStores = createSelector(
  selectPageStore,
  (pageStore) => selectCollectionIdToPageIdMap(pageStore)
);

/**
 * A collection item's page is its collection's detail page path plus its own
 * slug, so we resolve it once here (like the legacy rich-text link toolbar's
 * getHrefForPage does) instead of wiring a live resolver.
 */
export const resolveCollectionPageHref = (
  collectionIdToPageIdMap: Record<string, string>,
  hrefMap: Record<string, string>,
  collectionId: string | undefined
): string | undefined => {
  if (!collectionId) {
    return undefined;
  }
  const pageId = collectionIdToPageIdMap[collectionId];
  if (!pageId) {
    return undefined;
  }
  return hrefMap[pageId];
};

/**
 * A snapshot of the resolved href, taken at pick time — like the legacy
 * rich-text link toolbar's getHrefForPage, this won't follow later renames
 * of the collection's page or the item's slug.
 */
export const buildCollectionItemPageSlug = (
  collectionPageHref: string | undefined,
  itemSlug: string | undefined
): string | undefined => {
  if (!collectionPageHref || !itemSlug) {
    return undefined;
  }
  return `${collectionPageHref}/${itemSlug}`;
};

const useCollectionPageHref = (collectionId: string | undefined) => {
  const pageState = useDesignerSelector(selectPageStore);
  const collectionIdToPageIdMap = useDesignerSelector(
    selectCollectionIdToPageIdMapFromStores
  );

  // Resolved against the primary locale, not the current one — this picker's
  // pageSlug snapshot isn't locale-scoped, so a secondary-locale edit here
  // must not bake that locale's subdirectory into a value the primary locale
  // also reads (see ComponentPreview/_selectors.ts's hrefMap for the same
  // primary-locale convention).
  return React.useMemo(
    () =>
      resolveCollectionPageHref(
        collectionIdToPageIdMap,
        generateHrefMap(pageState)(null),
        collectionId
      ),
    [collectionId, collectionIdToPageIdMap, pageState]
  );
};

const useCollectionOptions = () => {
  const collections = useDesignerSelector(selectAllCollections);

  return React.useMemo(
    () =>
      collections
        .filterNot(
          (collection) => isMint(collection) || isSkusCollection(collection)
        )
        .valueSeq()
        .map((collection) => ({
          value: collection._id,
          label: collection.name,
        }))
        .toArray(),
    [collections]
  );
};

/**
 * Maps a CMS item to the picker's item option, including the draft flag the
 * `renderItemOption` prefix icon reads.
 */
export const toItemOption = (
  item: ItemRecordType,
  lastPublished: string | null
): ItemOption => {
  const itemId = item.get('_id');
  const slug = item.get('slug') || '';
  return {
    value: itemId,
    label: item.get('name') || slug || itemId,
    slug,
    isDraft: !isPublished(item, lastPublished),
  };
};

/**
 * The store can hold every item of a collection ever loaded elsewhere (e.g.
 * the CMS panel), which can be thousands for a large collection. Narrows
 * that down to the current search page's ids, plus the already-picked item
 * (which may not be on that page), instead of rendering the whole
 * collection as options.
 */
export const selectVisibleItemIds = (
  searchPageItems: ItemRecordType[],
  selectedItemId: string | undefined
): Set<string> => {
  const ids = new Set<string>(searchPageItems.map((item) => item.get('_id')));
  if (selectedItemId) {
    ids.add(selectedItemId);
  }
  return ids;
};

// useCollectionItemsPage falls back to a freshly-allocated {items: []} when
// there's no data (no collection selected, or the fetch hasn't resolved yet),
// so depending on its `data.items` directly would give the memo below a new
// dependency identity on every render in those states.
const EMPTY_SEARCH_PAGE_ITEMS: ItemRecordType[] = [];

const useItemOptions = (
  collectionId: string | undefined,
  itemId: string | undefined,
  query: string
) => {
  const collections = useDesignerSelector(selectAllCollections);
  const collection = collectionId ? collections.get(collectionId) : null;

  // Triggers the server-side search so matching items get fetched into the
  // store; the options below are read back from the store rather than this
  // response directly, since the raw response has one row per locale for
  // every item and would otherwise show duplicates.
  const {data: itemsPageData} = useCollectionItemsPage(collection ?? null, {
    format: 'itemRef',
    query,
  });
  const searchPageItems = itemsPageData.items.length
    ? itemsPageData.items
    : EMPTY_SEARCH_PAGE_ITEMS;

  // The already-picked item may not be on the first search page above (or
  // may predate this session entirely), in which case it's missing from
  // `options` and the Autocomplete renders the placeholder as though nothing
  // were selected. Fetch it directly so it's always present.
  useCollectionItem(collection ?? null, itemId ?? null, {format: 'itemRef'});

  const allItems = useDesignerSelector(selectCmsItemsForCurrentLocale);
  const lastPublished = useDesignerSelector(selectLastPublished);

  return React.useMemo<ItemOption[]>(() => {
    if (!collectionId) {
      return [];
    }
    const visibleItemIds = selectVisibleItemIds(searchPageItems, itemId);
    return getItemsForCollection(allItems, collectionId)
      .filter((item) => visibleItemIds.has(item.get('_id')))
      .map((item) => toItemOption(item, lastPublished))
      .toArray();
  }, [allItems, collectionId, itemId, searchPageItems, lastPublished]);
};

/**
 * Static (collectionId, itemId) picker rendered by LinkDynPageMode when the
 * element isn't in a context that supports live-binding `dynpage` to the
 * current/related item's page.
 */
export const LinkDynPageCollectionPicker = ({
  state,
  dispatch,
  isOverride,
}: {
  state: State;
  dispatch: (arg1?: any) => void;
  isOverride: boolean;
}) => {
  const collectionOptions = useCollectionOptions();
  const collectionPageHref = useCollectionPageHref(state.collectionId);

  const [itemQuery, setItemQuery] = React.useState('');
  const debouncedItemQuery = useDebouncedValue({value: itemQuery, wait: 200});
  const itemOptions = useItemOptions(
    state.collectionId,
    state.itemId,
    debouncedItemQuery
  );

  const onCollectionChange = React.useCallback(
    (newCollectionId: string, {changeType}: {changeType: string}) => {
      if (changeType !== 'changeEnd' || !newCollectionId) {
        return;
      }
      dispatch({
        payload: {
          ...state,
          collectionId: newCollectionId,
          itemId: undefined,
          pageSlug: undefined,
        },
        meta: {
          analytics: {
            type: 'element-settings-updated',
            value: 'collection page - collection',
          },
        },
      });
    },
    [dispatch, state]
  );

  const onItemChange = React.useCallback(
    (newItemId: string | null) => {
      if (!newItemId) {
        return;
      }
      const selectedItem = itemOptions.find(
        (option) => option.value === newItemId
      );
      const pageSlug = buildCollectionItemPageSlug(
        collectionPageHref,
        selectedItem?.slug
      );
      dispatch({
        payload: {...state, itemId: newItemId, pageSlug},
        meta: {
          analytics: {
            type: 'element-settings-updated',
            value: 'collection page - item',
          },
        },
      });
      setItemQuery('');
    },
    [dispatch, state, itemOptions, collectionPageHref]
  );

  const renderItemOption = React.useCallback(
    (option: ItemOption) => (
      <AutocompleteItem
        colorVariant={option.isDraft ? 'orange' : 'purple'}
        prefix={option.isDraft ? <PageDraftIcon /> : <PageDefaultIcon />}
      >
        <AutocompleteItemBody>{option.label}</AutocompleteItemBody>
      </AutocompleteItem>
    ),
    []
  );

  return (
    <>
      {/* Collection */}
      <Label
        data-automation-id="symbols-dataType-contentType"
        isOverride={isOverride}
      >
        Collection
      </Label>
      <Select
        data-automation-id="Link-collectionPage-collection"
        aria-label="Collection"
        value={state.collectionId ?? null}
        options={collectionOptions}
        placeholder="Select a collection..."
        onChange={onCollectionChange}
      />
      {/* Page */}
      <Label
        data-automation-id="symbols-dataType-contentType"
        isOverride={isOverride}
      >
        Page
      </Label>
      <Autocomplete
        data-automation-id="Link-collectionPage-item"
        aria-label="Page"
        onInputChange={setItemQuery}
        onChange={onItemChange}
        options={itemOptions}
        value={state.itemId || undefined}
        placeholder={
          state.collectionId
            ? 'Search by item name'
            : 'Select a collection first'
        }
        isDisabled={!state.collectionId}
        isClearable={false}
        renderOption={renderItemOption}
      />
      {state.collectionId && !collectionPageHref && (
        <View gridColumn="1/-1">
          <Note colorVariant="warning" title="This collection has no page">
            Create a page for this collection to link to its items.
          </Note>
        </View>
      )}
      {state.collectionId && state.itemId && collectionPageHref && (
        <View gridColumn="1/-1">
          <Note colorVariant="warning" title="This link may break">
            Renaming this item&apos;s slug will break the link.
          </Note>
        </View>
      )}
    </>
  );
};
